import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DefaultApiType } from '@app/core/models/core-models';
import { ContentLockerCodesReposne, ContentLockerDomainsResponse } from '@app/core/services/content-locker/content-locker.types';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CodesModalLockerTypes, ICodesParams } from './codes-modal.component';

export interface AvailableCodeOption {
  systemName: string;
  label: string;
  checked: boolean;
  additionalTemplate: string | null;
  additionalTemplateValues?: { systemName: string; label: string; value: string }[];
}

@Injectable()
export class CodesModalService {
  public availableCodeOptions: Record<string, AvailableCodeOption[]> = {
    contentlocker: [
      { systemName: 'adblock_detection', label: 'adblock_detection', checked: false, additionalTemplate: null },
      {
        systemName: 'javascript_disabled_detection',
        label: 'detecting_unavailability_of_js',
        checked: false,
        additionalTemplate: null
      },
      {
        systemName: 'javascript_dynamic_options',
        label: 'adjust_dynamic_parameters_of_js',
        checked: false,
        additionalTemplate: 'jsParams',
        additionalTemplateValues: [
          { systemName: 'title', label: 'desktop_version_name', value: '' },
          { systemName: 'title_mobile', label: 'mobile_version_name', value: '' },
          { systemName: 'desc', label: 'desktop_version_desc', value: '' },
          { systemName: 'desc_mobile', label: 'mobile_version_desc', value: '' }
        ]
      },
      {
        systemName: 'tracking_parameters',
        label: 'add_parameters_tracking_content',
        checked: false,
        additionalTemplate: 'trackingParams',
        additionalTemplateValues: [
          { systemName: 'ml_sub1', label: 'parametr_ml_sub1', value: '' },
          { systemName: 'ml_sub2', label: 'parametr_ml_sub2', value: '' },
          { systemName: 'ml_sub3', label: 'parametr_ml_sub3', value: '' },
          { systemName: 'ml_sub4', label: 'parametr_ml_sub4', value: '' },
          { systemName: 'ml_sub5', label: 'parametr_ml_sub5', value: '' }
        ]
      },
      {
        systemName: 'auto_translation',
        label: 'auto_translation_of_locker_content',
        checked: false,
        additionalTemplate: null
      }
    ],
    captchalocker: [
      {
        systemName: 'tracking_parameters',
        label: 'add_parameters_tracking_content',
        checked: false,
        additionalTemplate: 'trackingParams',
        additionalTemplateValues: [
          { systemName: 'ml_sub1', label: 'parametr_ml_sub1', value: '' },
          { systemName: 'ml_sub2', label: 'parametr_ml_sub2', value: '' },
          { systemName: 'ml_sub3', label: 'parametr_ml_sub3', value: '' },
          { systemName: 'ml_sub4', label: 'parametr_ml_sub4', value: '' },
          { systemName: 'ml_sub5', label: 'parametr_ml_sub5', value: '' }
        ]
      }
    ],
    filelocker: [
      {
        systemName: 'javascript_dynamic_options',
        label: 'adjust_dynamic_parameters_of_js',
        checked: false,
        additionalTemplate: 'jsParams',
        additionalTemplateValues: [{ systemName: 'title', label: 'desktop_version_name', value: '' }]
      },
      {
        systemName: 'tracking_parameters',
        label: 'add_parameters_tracking_content',
        checked: false,
        additionalTemplate: 'trackingParams',
        additionalTemplateValues: [
          { systemName: 'ml_sub1', label: 'parametr_ml_sub1', value: '' },
          { systemName: 'ml_sub2', label: 'parametr_ml_sub2', value: '' },
          { systemName: 'ml_sub3', label: 'parametr_ml_sub3', value: '' },
          { systemName: 'ml_sub4', label: 'parametr_ml_sub4', value: '' },
          { systemName: 'ml_sub5', label: 'parametr_ml_sub5', value: '' }
        ]
      },
      {
        systemName: 'auto_translation',
        label: 'auto_translation_of_locker_content',
        checked: false,
        additionalTemplate: null
      }
    ],
    mobileRewards: [
      {
        systemName: 'tracking_parameters',
        label: 'add_parameters_tracking_content',
        checked: false,
        additionalTemplate: 'trackingParams',
        additionalTemplateValues: [
          { systemName: 'ml_sub1', label: 'parametr_ml_sub1', value: '' },
          { systemName: 'ml_sub2', label: 'parametr_ml_sub2', value: '' },
          { systemName: 'ml_sub3', label: 'parametr_ml_sub3', value: '' },
          { systemName: 'ml_sub4', label: 'parametr_ml_sub4', value: '' },
          { systemName: 'ml_sub5', label: 'parametr_ml_sub5', value: '' }
        ]
      }
    ],
    offerwall: [
      {
        systemName: 'add_player_id',
        label: 'add_player_id',
        checked: false,
        additionalTemplate: 'jsParams',
        additionalTemplateValues: [{ systemName: 'player_id', label: 'player_id_text', value: '' }]
      },
      {
        systemName: 'tracking_parameters',
        label: 'add_parameters_tracking_content',
        checked: false,
        additionalTemplate: 'trackingParams',
        additionalTemplateValues: [
          { systemName: 'ml_sub1', label: 'parametr_ml_sub1', value: '' },
          { systemName: 'ml_sub2', label: 'parametr_ml_sub2', value: '' },
          { systemName: 'ml_sub3', label: 'parametr_ml_sub3', value: '' },
          { systemName: 'ml_sub4', label: 'parametr_ml_sub4', value: '' },
          { systemName: 'ml_sub5', label: 'parametr_ml_sub5', value: '' }
        ]
      }
    ]
  };

  constructor(private readonly http: HttpClient) {}

  public getDomains(lockerId: string): Observable<DefaultApiType<ContentLockerDomainsResponse>> {
    return this.http.get<DefaultApiType<ContentLockerDomainsResponse>>(environment.apiUrl + `/offer_locker/${lockerId}/domains`, {});
  }

  public getLockerCodes(lockerId: string, requestParams: ICodesParams): Observable<ContentLockerCodesReposne> {
    let params = new HttpParams()
      .set('domain_id', requestParams.domain_id)
      .set('adblock_detection', requestParams.adblock_detection)
      .set('javascript_disabled_detection', requestParams.javascript_disabled_detection)
      .set('javascript_dynamic_options', requestParams.javascript_dynamic_options)
      .set('tracking_parameters', requestParams.tracking_parameters)
      .set('add_player_id', requestParams.add_player_id)
      .set('auto_translation', requestParams.auto_translation);

    if (!requestParams.player_id.length && requestParams.add_player_id === 1) {
      params = params.set('add_player_id', 0);
    }

    if (requestParams.player_id.length > 0) {
      params = params.set('player_id', requestParams.player_id);
    }
    if (requestParams.ml_sub1.length > 0) {
      params = params.set('ml_sub1', requestParams.ml_sub1);
    }
    if (requestParams.ml_sub2.length > 0) {
      params = params.set('ml_sub2', requestParams.ml_sub2);
    }
    if (requestParams.ml_sub3.length > 0) {
      params = params.set('ml_sub3', requestParams.ml_sub3);
    }
    if (requestParams.ml_sub4.length > 0) {
      params = params.set('ml_sub4', requestParams.ml_sub4);
    }
    if (requestParams.ml_sub5.length > 0) {
      params = params.set('ml_sub5', requestParams.ml_sub5);
    }
    if (requestParams.title.length > 0) {
      params = params.set('title', requestParams.title);
    }
    if (requestParams.title_mobile.length > 0) {
      params = params.set('title_mobile', requestParams.title_mobile);
    }
    if (requestParams.desc.length > 0) {
      params = params.set('desc', requestParams.desc);
    }
    if (requestParams.desc_mobile.length > 0) {
      params = params.set('desc_mobile', requestParams.desc_mobile);
    }
    return this.http.get<DefaultApiType<ContentLockerCodesReposne>>(environment.apiUrl + `/offer_locker/${lockerId}/codes`, { params }).pipe(
      map(({ data }) => {
        if (data) {
          return data;
        }

        return {};
      })
    );
  }

  public getModalHeader = (lockerType: CodesModalLockerTypes): string => {
    const translationsMap = {
      contentlocker: 'cpa_locker',
      captchalocker: 'captcha_locker',
      filelocker: 'file_locker',
      mobileRewards: 'mobile_rewards',
      offerwall: 'offer_wall_rewards'
    };

    return 'menu_translations.' + translationsMap[lockerType];
  };
}
